<template>
  <div class="accounting-reports-page">
    <div class="reports-page-toolbar">
      <div class="toolbar-title">
        <div class="title-icon">
          <v-icon size="18" color="white">mdi-file-chart-outline</v-icon>
        </div>
        <div class="title-text">
          <h2>회계보고서류</h2>
          <p>장부 · 회계분석 · 품의·결의서</p>
        </div>
      </div>

      <div class="toolbar-banks">
        <button
          type="button"
          class="bank-chip"
          :class="{ active: selectedBankCode === null }"
          @click="selectBank(null)"
        >
          전체
        </button>
        <button
          v-for="bank in activeBankList"
          :key="bank.mb_code"
          type="button"
          class="bank-chip"
          :class="{ active: selectedBankCode === bank.mb_code }"
          @click="selectBank(bank.mb_code)"
        >
          {{ bank.mb_alias || bank.mb_bank_name }}
        </button>
        <span v-if="bankList.length === 0 && !isLoading" class="bank-empty">등록된 통장이 없습니다</span>
      </div>
    </div>

    <div v-if="isLoading" class="reports-page-loading">
      <v-progress-circular indeterminate color="primary" size="40" />
      <p>보고 자료를 불러오는 중...</p>
    </div>

    <div v-else class="reports-page-body">
      <BankbookReportsModal
        page
        :member-code="String(memberCode || '')"
        :current-year="currentYear"
        :current-month="currentMonth"
        :biz-gubun="selectedBizGubun"
        :bank-code="selectedBankCode"
        :bank-label="selectedBankAlias"
        :cashbook-list="reportCashbookList"
        :prev-month-balance="prevMonthBalance"
        :income-code-list="incomeCodeList"
        :expense-code-list="expenseCodeList"
        :beneficiary-matches="beneficiaryMatches"
        @change-year-month="onChangeYearMonth"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject, onMounted, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMemberYearMonthOptional } from '@/composables/useMemberYearMonth';
import { bankbookApi, beneficiaryApi, collectionApi } from '@/services/api';
import BankbookReportsModal from '@/components/bankbook/BankbookReportsModal.vue';

interface BankData {
  mb_code: string;
  mb_alias: string;
  mb_bank_name: string;
  mb_biz_gubun?: string;
  mb_start_date?: string;
  mb_end_date?: string;
}

interface AccountCode {
  gubun: 'IN' | 'OUT';
  scode: string;
  sname: string;
}

interface CashbookItem {
  cb_idx?: number;
  cb_bank_code?: string;
  cb_scode?: string;
  cb_memo?: string;
  cb_price?: number;
  cb_matched_beneficiary_idx?: number | null;
  split_count?: number;
}

const route = useRoute();
const memberData = inject<any>('memberData');
const selectedBizGubunRef = inject<Ref<string>>('selectedBizGubun');
const selectedBizGubun = computed(() => selectedBizGubunRef?.value || '');

const memberCode = computed(() => {
  return route.params.memberCode as string || memberData?.value?.m_code || 0;
});

const memberCodeForYm = computed(() => String(memberCode.value || ''));
const memberYearMonth = useMemberYearMonthOptional(memberCodeForYm);
const currentYear = computed(() => String(memberYearMonth.selectedYear.value));
const currentMonth = computed(() => String(memberYearMonth.selectedMonth.value).padStart(2, '0'));

const isLoading = ref(true);
const initialDataLoaded = ref(false);

const bankList = ref<BankData[]>([]);
const selectedBankCode = ref<string | null>(null);
const accountCodes = ref<AccountCode[]>([]);
const cashbookList = ref<CashbookItem[]>([]);
const prevMonthBalance = ref(0);
const beneficiaryMatches = ref<Record<number, { cb_idx: number; cb_name: string; matchType: string }>>({});

const incomeCodeList = computed(() => accountCodes.value.filter((ac) => ac.gubun === 'IN'));
const expenseCodeList = computed(() => accountCodes.value.filter((ac) => ac.gubun === 'OUT'));

const isBankActiveForCurrentMonth = (bank: BankData): boolean => {
  const currentYM = `${currentYear.value}-${currentMonth.value}`;
  if (!bank.mb_start_date) return true;
  if (currentYM < bank.mb_start_date) return false;
  if (bank.mb_end_date && currentYM > bank.mb_end_date) return false;
  return true;
};

const activeBankList = computed(() => bankList.value.filter((bank) => isBankActiveForCurrentMonth(bank)));

const selectedBankAlias = computed(() => {
  if (!selectedBankCode.value) return '';
  const bank = bankList.value.find((b) => b.mb_code === selectedBankCode.value);
  return bank?.mb_alias || bank?.mb_bank_name || '통장';
});

const reportCashbookList = computed(() => {
  if (!selectedBankCode.value) return cashbookList.value;
  return cashbookList.value.filter((item) => item.cb_bank_code === selectedBankCode.value);
});

const selectBank = (bankCode: string | null) => {
  selectedBankCode.value = bankCode;
};

const onChangeYearMonth = (year: string, month: string) => {
  memberYearMonth.setYearMonth(parseInt(year, 10), parseInt(month, 10));
};

const loadBankList = async () => {
  if (!memberCode.value) return;
  try {
    const res = await bankbookApi.getBankList(memberCode.value, true);
    let allBanks = (res.data?.data || []) as BankData[];
    if (selectedBizGubun.value) {
      allBanks = allBanks.filter((b) => b.mb_biz_gubun === selectedBizGubun.value);
    }
    bankList.value = allBanks;
    if (selectedBankCode.value !== null) {
      const exists = activeBankList.value.some((b) => b.mb_code === selectedBankCode.value);
      if (!exists) selectedBankCode.value = null;
    }
  } catch (error) {
    console.error('통장 목록 로드 오류:', error);
    bankList.value = [];
  }
};

const loadAccountCodes = async () => {
  try {
    const res = await bankbookApi.getAccountCodes(currentYear.value);
    accountCodes.value = (res.data?.data || []) as AccountCode[];
  } catch (error) {
    console.error('계정코드 로드 오류:', error);
  }
};

const loadCarryover = async () => {
  const monthNum = parseInt(currentMonth.value, 10);
  const yearNum = parseInt(currentYear.value, 10);
  if (monthNum <= 1 || !memberCode.value) {
    prevMonthBalance.value = 0;
    return;
  }
  try {
    const res = await bankbookApi.getCumulativeCarryover(
      memberCode.value,
      String(yearNum),
      String(monthNum).padStart(2, '0'),
      selectedBizGubun.value || undefined,
      selectedBankCode.value || undefined
    );
    prevMonthBalance.value = Number(res.data?.data?.balance) || 0;
  } catch (error) {
    console.error('이월금 조회 오류:', error);
    prevMonthBalance.value = 0;
  }
};

const matchBeneficiaries = async () => {
  if (!memberCode.value) {
    beneficiaryMatches.value = {};
    return;
  }

  const targetItems = cashbookList.value
    .filter((item) => item.cb_scode === '112')
    .map((item) => ({
      idx: item.cb_idx!,
      memo: item.cb_memo || '',
      price: item.cb_price || 0,
      matchedBeneficiaryIdx: item.cb_matched_beneficiary_idx,
    }));

  if (targetItems.length === 0) {
    beneficiaryMatches.value = {};
    return;
  }

  try {
    const collectionRes = await collectionApi.getByMember(memberCode.value, {
      year: parseInt(currentYear.value, 10),
      month: parseInt(currentMonth.value, 10),
      bizGubun: selectedBizGubun.value || undefined,
    });
    const collectionData = collectionRes.data?.success
      ? (collectionRes.data.data?.list || collectionRes.data.data || [])
      : [];

    const beneficiaryNameMap = new Map<number, string>();
    collectionData.forEach((item: any) => {
      if (item.cb_idx && item.cb_name) {
        beneficiaryNameMap.set(item.cb_idx, item.cb_name);
      }
    });

    const finalMatches: Record<number, { cb_idx: number; cb_name: string; matchType: string }> = {};
    const itemsNeedingAutoMatch: Array<{ idx: number; memo: string; price: number }> = [];

    targetItems.forEach((item) => {
      if (item.matchedBeneficiaryIdx) {
        finalMatches[item.idx] = {
          cb_idx: item.matchedBeneficiaryIdx,
          cb_name: beneficiaryNameMap.get(item.matchedBeneficiaryIdx) || '',
          matchType: 'manual',
        };
      } else if (item.memo) {
        itemsNeedingAutoMatch.push({ idx: item.idx, memo: item.memo, price: item.price });
      }
    });

    if (itemsNeedingAutoMatch.length > 0) {
      const res = await beneficiaryApi.matchFromMemo(Number(memberCode.value), itemsNeedingAutoMatch);
      const memoMatches = res.data?.success ? (res.data.data || {}) : {};
      Object.entries(memoMatches).forEach(([cbIdxStr, match]: [string, any]) => {
        const cashbookIdx = parseInt(cbIdxStr, 10);
        if (!finalMatches[cashbookIdx]) {
          finalMatches[cashbookIdx] = match;
        }
      });
    }

    beneficiaryMatches.value = finalMatches;
  } catch (error) {
    console.error('수급자 매칭 오류:', error);
    beneficiaryMatches.value = {};
  }
};

const loadCashbookData = async () => {
  if (!memberCode.value) return;
  try {
    const res = await bankbookApi.getListWithSplits(memberCode.value, {
      bizGubun: selectedBizGubun.value || undefined,
      year: currentYear.value,
      month: currentMonth.value,
    });
    const data = res.data?.data || [];
    cashbookList.value = data.map((item: CashbookItem) => ({
      ...item,
      cb_price: Number(item.cb_price) || 0,
      split_count: Number(item.split_count) || 0,
    }));
    await loadCarryover();
    await matchBeneficiaries();
  } catch (error) {
    console.error('통장내역 데이터 로드 오류:', error);
    cashbookList.value = [];
    prevMonthBalance.value = 0;
    beneficiaryMatches.value = {};
  }
};

const loadInitialData = async () => {
  if (initialDataLoaded.value) return;
  initialDataLoaded.value = true;
  isLoading.value = true;
  try {
    await loadBankList();
    await loadAccountCodes();
    await loadCashbookData();
  } finally {
    isLoading.value = false;
  }
};

watch(selectedBizGubun, async (newBizGubun, oldBizGubun) => {
  if (newBizGubun && !initialDataLoaded.value) {
    await loadInitialData();
    return;
  }
  if (newBizGubun !== oldBizGubun && initialDataLoaded.value) {
    await loadBankList();
    await loadCashbookData();
  }
}, { immediate: true });

watch([currentYear, currentMonth], async () => {
  if (!initialDataLoaded.value) return;
  if (selectedBankCode.value) {
    const selectedBank = bankList.value.find((b) => b.mb_code === selectedBankCode.value);
    if (selectedBank && !isBankActiveForCurrentMonth(selectedBank)) {
      selectedBankCode.value = null;
    }
  }
  await loadAccountCodes();
  await loadCashbookData();
});

watch(selectedBankCode, async () => {
  if (!initialDataLoaded.value) return;
  await loadCarryover();
});

onMounted(async () => {
  if (selectedBizGubun.value && !initialDataLoaded.value) {
    await loadInitialData();
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/pages/accounting-reports';
</style>
